import {
    Button,
    Column,
    Container,
    DatePricker,
    Label,
    Loader,
    ModalWindow,
    Option,
    Select,
    TextInput,
    Title,
} from 'components';
import {
    ExpertArea,
    ExpertCertificate,
    GetExpertCertificates,
    UpdateExpertArea,
    UpdateExpertCertificate,
} from 'models/expert-certificates';
import React, { FC, useEffect, useState } from 'react';
import { AdminExpertCertificateService } from 'services/expert-certificate.service';
import { AreasGrid } from './AreaGrid';
import { EditArea } from './EditArea';
import { CertificatesGrid } from './CertificatesGrid';
import { CertificationTypeFilter } from 'enums/certification-type';
import { useUrlQueryParamsForGrid } from 'hooks/useUrlQueryParamsForGrid';
import SelectByEnum from 'components/SelectByEnum/SelectByEnum';
import { EditCertificate } from './EditCertificate';
import { useAlert } from 'react-alert';

const KExport: FC = () => {
    const alert = useAlert();

    const [loading, setLoading] = useState(true);
    const [areas, setAreas] = useState<ExpertArea[]>([]);
    const [editArea, setEditArea] = useState<UpdateExpertArea>();
    const [editCertificate, setEditCertificate] = useState<UpdateExpertCertificate>();
    const [count, setCount] = useState<number>(0);
    const [data, setData] = useState<ExpertCertificate[]>([]);

    const getDateStart = (): string => {
        const date = new Date();
        date.setDate(date.getDate() - 300 * 8);
        date.setHours(0, 0, 0);
        return date.toISOString();
    };

    const [expertAreaId, setExpertAreaId] = useState<Option>();
    const [startDate, setStartDate] = useState<string>(getDateStart());
    const [finishDate, setFinishDate] = useState<string>(new Date().toISOString());
    const [active, setActive] = useState<CertificationTypeFilter>();
    const [email, setEmail] = useState<string>();

    useEffect(() => {
        AdminExpertCertificateService.getAreas()
            .then((d) => {
                setAreas(d.data);
            })
            .finally(() => {
                setLoading(false);
            });
    }, []);

    const handleAddArea = (val: UpdateExpertArea) => {
        setLoading(true);
        setEditArea(undefined);
        AdminExpertCertificateService.addArea(val)
            .then((d) => {
                setAreas([d.data, ...areas]);
                alert.success('Экспертная область добавлена');
            })
            .finally(() => setLoading(false));
    };
    const handleUpdateArea = (val: UpdateExpertArea) => {
        setLoading(true);
        setEditArea(undefined);
        AdminExpertCertificateService.updateArea(val)
            .then((d) => {
                const index = areas.findIndex((r) => r.id == val.id);

                if (index < 0) return;

                areas[index] = d.data;
                setAreas([...areas]);
                alert.success('Экспертная область обновлена');
            })
            .finally(() => setLoading(false));
    };
    const handleDeleteArea = (val: string) => {
        if (!confirm('Are you sure?')) return;

        setLoading(true);
        setEditArea(undefined);
        AdminExpertCertificateService.deleteArea(val)
            .then(() => {
                setAreas([...areas.filter((r) => r.id !== val)]);
                alert.success('Экспертная область удалена');
            })
            .catch(() => {
                alert.error('Экспертная область не может быть удалена, скорее всего есть сертификаты у данной области');
            })
            .finally(() => setLoading(false));
    };

    const handleSearch = () => {
        setLoading(true);
        AdminExpertCertificateService.getCertificates(getParams())
            .then((d) => {
                setCount(d.data.count);
                setData(d.data.items);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const getParamsWithoutPagination = (): GetExpertCertificates => ({
        expertAreaId: expertAreaId?.value as string,
        startDate,
        finishDate,
        active,
        email,
    });

    const getParams = (): GetExpertCertificates => ({
        ...getParamsWithoutPagination(),
        page: pagination.page,
        size: pagination.size,
    });

    const { pagination } = useUrlQueryParamsForGrid({
        total: count,
        clearPaginationDeps: Object.values(getParamsWithoutPagination()),
        loadFn: handleSearch,
    });

    const handleAddCertificate = (val: UpdateExpertCertificate) => {
        setLoading(true);
        setEditCertificate(undefined);
        AdminExpertCertificateService.addCertificate(val)
            .then((d) => {
                setData([d.data, ...data]);
                alert.success('Сертификат добавлен');
            })
            .finally(() => setLoading(false));
    };
    const handleUpdateCertificate = (val: UpdateExpertCertificate) => {
        setLoading(true);
        setEditCertificate(undefined);
        AdminExpertCertificateService.updateCertificate(val)
            .then((d) => {
                const index = data.findIndex((r) => r.id == val.id);

                if (index < 0) return;

                data[index] = d.data;
                setData([...data]);
                alert.success('Сертификат обновлён');
            })
            .finally(() => setLoading(false));
    };

    const downloadXls = () => {
        setLoading(true);
        AdminExpertCertificateService.getCertificatesXls(getParamsWithoutPagination()).finally(() => {
            setLoading(false);
        });
    };

    return (
        <>
            {loading && <Loader isBlock />}
            <Container>
                <ModalWindow onCloseClick={() => setEditArea(undefined)} isOpen={!!editArea}>
                    <EditArea data={editArea} add={handleAddArea} update={handleUpdateArea} />
                </ModalWindow>
                <Column columnSize="50">
                    <Title titleText="Область экспертизы" />
                </Column>
                <Container additionalClass="container--items-end">
                    <Column columnSize="50" className="text-left bottom-space-3x">
                        <Button
                            buttonClass="primary"
                            handleClick={() =>
                                setEditArea({
                                    name: '',
                                })
                            }
                        >
                            Добавить
                        </Button>
                    </Column>
                </Container>
                <Column columnSize="100" className="top-space-10x">
                    <AreasGrid data={areas} setUpdate={setEditArea} onDelete={handleDeleteArea} />
                </Column>
            </Container>
            <Container>
                <Column columnSize="100">
                    <hr />
                </Column>
            </Container>
            <Container>
                <ModalWindow onCloseClick={() => setEditCertificate(undefined)} isOpen={!!editCertificate} size="big">
                    <EditCertificate
                        areas={areas}
                        data={editCertificate}
                        add={handleAddCertificate}
                        update={handleUpdateCertificate}
                    />
                </ModalWindow>
                <Column columnSize="50">
                    <Title titleText="Сертификаты" />
                </Column>
                <Container additionalClass="container--items-end">
                    <Column columnSize="20" className="p-right-space-3x">
                        <Label labelText="Дата начала" labelFor="" />
                        <DatePricker
                            name="dateStart"
                            value={startDate}
                            returnValue="start"
                            onChange={(date) => setStartDate(date as string)}
                        />
                    </Column>
                    <Column columnSize="20" className="p-right-space-3x">
                        <Label labelText="Дата окончания" labelFor="" />
                        <DatePricker
                            name="dateEnd"
                            value={finishDate}
                            returnValue="end"
                            onChange={(date) => setFinishDate(date as string)}
                        />
                    </Column>
                    <Column columnSize="20" className="p-right-space-3x top-space-10x">
                        <fieldset className="form-group">
                            <Label labelText="Статус сертификата" labelFor="" />
                            <SelectByEnum
                                hasEmpty
                                enumOptions={CertificationTypeFilter}
                                selectedId={active}
                                onChange={(v) => setActive(v)}
                            />
                        </fieldset>
                    </Column>
                    <Column columnSize="30" className="p-right-space-3x top-space-10x">
                        <TextInput
                            id="emailId"
                            label="Email"
                            value={email}
                            onChangeHandler={(e) => setEmail(e.target.value)}
                        />
                    </Column>
                    <Column columnSize="20" className="p-right-space-3x top-space-10x">
                        <fieldset className="form-group">
                            <Label labelText="Область экспертизы" labelFor="" />
                            <Select
                                options={
                                    expertAreaId
                                        ? [
                                              { label: 'Не выбрано', value: '' },
                                              ...areas.map((t) => ({ label: t.name, value: t.id })),
                                          ]
                                        : areas.map((t) => ({ label: t.name, value: t.id }))
                                }
                                onChange={(v) => setExpertAreaId(v[0])}
                                values={expertAreaId ? [expertAreaId] : [{ label: 'Не выбрано', value: '' }]}
                            />
                        </fieldset>
                    </Column>
                </Container>
                <Container additionalClass="container--items-end">
                    <Column columnSize="100" className="text-left bottom-space-3x">
                        <Button buttonClass="primary" handleClick={handleSearch}>
                            Поиск
                        </Button>
                        <Button
                            buttonClass="primary"
                            handleClick={() => {
                                if (areas.length === 0) {
                                    alert.error('Область экспертизы пустая');
                                    return;
                                }
                                setEditCertificate({
                                    ownerName: '',
                                    isDeleted: false,
                                    userId: '',
                                });
                            }}
                        >
                            Добавить
                        </Button>
                        <Button buttonClass="primary" handleClick={downloadXls}>
                            Excel
                        </Button>
                    </Column>
                </Container>
                <Column columnSize="100" className="top-space-10x">
                    <CertificatesGrid data={data} setUpdate={setEditCertificate} pagination={pagination} />
                </Column>
            </Container>
        </>
    );
};

export default KExport;
